import { useEffect, useMemo, useRef, useState } from 'react'

import { useRemoveRule } from '../hooks/useRemoveRule'
import { useUpdateRule } from '../hooks/useUpdateRule'
import {
  Column,
  useFlexLayout,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import { FixedSizeList } from 'react-window'

import { Box, Table, Tbody } from '@chakra-ui/react'

import { ConfirmRemovalDialog, TableRowsSkeleton } from '@/components/ui'
import { RuleFilterInput } from '@/graphql/generated/schemas'
import { getSortedColumn } from '@/utils/pageNavigation'

import {
  ROW_HEIGHT,
  RulesRefetchType,
  RulesType,
  createItemData,
  getOrderBy,
} from '../utils/utils'
import { TableFooter } from './TableFooter'
import { TableHeaders } from './TableHeaders'
import { TableRow } from './TableRow'

export type DataTableProps<DataT> = {
  data: DataT[]
  columns: Column<DataT>[]
  refetch: RulesRefetchType
  total?: number
  filters?: RuleFilterInput
  pageSize?: number
  onPageResize?: (pageIndex: number) => void
  onRowClick?: (id: string, isV2: boolean, event: React.MouseEvent) => void
  isLoadingExternal?: boolean
}

export const DataTable = <DataT,>({
  data,
  columns,
  total,
  pageSize: initialPageSize,
  onPageResize,
  onRowClick,
  refetch,
  isLoadingExternal = false,
}: DataTableProps<DataT>) => {
  const ref = useRef(null)
  const listRef = useRef(null)
  const { updateRuleStatus } = useUpdateRule()
  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 80,
      maxWidth: 200,
    }),
    []
  )
  const skipPageResetRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  // Parent already pre-fetches on mount; skip the redundant refetch on first render.
  const isInitialMount = useRef(true)
  const {
    isArchiveRuleLoading,
    isOpen,
    onClose,
    onOpen,
    setSelectedRule,
    handleRemoveRule,
    selectedRule,
  } = useRemoveRule()

  const {
    getTableProps,
    headerGroups,
    rows,
    columns: tableColumns,
    prepareRow,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      autoResetPage: !skipPageResetRef.current,
      columns,
      data,
      defaultColumn,
      initialState: {
        pageIndex: 0,
        pageSize: initialPageSize,
        sortBy: [
          {
            id: 'createdAt',
            desc: true,
          },
        ],
      },
      manualPagination: true,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      pageCount: -1,
    },
    useFlexLayout,
    useSortBy,
    usePagination
  )
  const pageCount = Math.ceil(total / pageSize)
  const sortedColumn = getSortedColumn(tableColumns)

  useEffect(() => {
    if (isInitialMount.current) return
    skipPageResetRef.current = true
    setIsLoading(true)
    refetchRules()
  }, [pageIndex, pageSize, JSON.stringify(sortedColumn)])

  useEffect(() => {
    isInitialMount.current = false
  }, [])

  const refetchRules = async () => {
    refetch({
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortedColumn),
    }).then(() => {
      setIsLoading(false)
      skipPageResetRef.current = false
    })
  }

  const handleToggleSwitch = async (rule: RulesType) => {
    await updateRuleStatus(rule)
  }

  const handleOnRemove = async (rule: RulesType) => {
    setSelectedRule(rule)
    onOpen()
  }

  const itemData = createItemData(
    rows,
    prepareRow,
    handleToggleSwitch,
    handleOnRemove,
    onRowClick
  )

  return (
    <Box mb='50px' w={{ base: '1000px', lg: 'full' }}>
      <Table
        {...getTableProps()}
        bgColor='white'
        border='none'
        borderWidth='3px'
        data-testid='sopsPage_table'
        overflow='hidden'
        ref={listRef}
        rounded={{ base: 'none', lg: 'lg' }}
        shadow='0px 2px 5px 6px rgba(0, 0, 0, 0.1)'
      >
        <TableHeaders headerGroups={headerGroups} />

        {isLoading || isLoadingExternal ? (
          <TableRowsSkeleton
            columnCount={tableColumns.length}
            rowCount={pageSize}
            rowHeight={`${ROW_HEIGHT}px`}
          />
        ) : (
          <Tbody ref={ref}>
            <FixedSizeList
              height={data.length * ROW_HEIGHT}
              itemCount={rows.length}
              itemData={itemData}
              itemSize={ROW_HEIGHT}
            >
              {TableRow}
            </FixedSizeList>
          </Tbody>
        )}
        <TableFooter
          gotoPage={gotoPage}
          nextPage={nextPage}
          onPageResize={onPageResize}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          previousPage={previousPage}
          setPageSize={setPageSize}
          total={total}
        />
      </Table>

      {isOpen && (
        <ConfirmRemovalDialog
          actionButtonText='Confirm Archival'
          bodyText='Are you sure you want to archive '
          handleOnClick={handleRemoveRule}
          isLoading={isArchiveRuleLoading}
          isOpen={isOpen}
          itemTitle={selectedRule?.name}
          onClose={onClose}
          title='Archive Rule'
        />
      )}
    </Box>
  )
}
